class CustomSwatch extends HTMLElement {
  constructor() {
    super();
    this.boundHandlers = {
      dotClick: this.handleDotClick.bind(this),
    };
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  static get observedAttributes() {
    return ["id", "selected", "disabled"];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) {
      this.render();
    }
  }

  // Getters and Setters
  get swatchId() {
    return this.getAttribute("id");
  }

  set swatchId(value) {
    this.setAttribute("id", value);
  }

  get selected() {
    return this.hasAttribute("selected");
  }

  set selected(value) {
    if (value) {
      this.setAttribute("selected", "");
    } else {
      this.removeAttribute("selected");
    }
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }

  set disabled(value) {
    if (value) {
      this.setAttribute("disabled", "");
    } else {
      this.removeAttribute("disabled");
    }
  }

  render() {
    const id = this.swatchId;
    this.setAttribute("role", "group");
    this.setAttribute("aria-label", `Swatch controller ${id}`);
  }

  setupEventListeners() {
    const dots = this.querySelectorAll(".embla__dot");

    if (dots.length === 0) {
      console.warn(`No dots found for swatch ${this.swatchId}`);
      return;
    }

    dots.forEach((dot, index) => {
      dot.addEventListener("click", this.boundHandlers.dotClick);

      // Add accessibility attributes
      dot.setAttribute("role", "button");
      dot.setAttribute("tabindex", "0");
      dot.setAttribute("aria-label", `Select variant ${index + 1}`);

      // Add keyboard support
      dot.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.boundHandlers.dotClick(event);
        }
      });
    });
  }

  handleDotClick(event) {
    const { target } = event;
    const slideId = target.getAttribute("data-slide-id");

    if (!slideId) {
      console.warn("No slide ID found on clicked dot");
      return;
    }

    // Prevent multiple rapid clicks
    if (target.classList.contains("processing")) {
      return;
    }

    target.classList.add("processing");

    try {
      this.updateSwatchState(target, slideId);
    } catch (error) {
      console.error("Error updating swatch state:", error);
    } finally {
      // Remove processing class after a short delay
      setTimeout(() => {
        target.classList.remove("processing");
      }, 100);
    }
  }

  updateSwatchState(clickedDot, slideId) {
    // Update dot states
    this.updateDotStates(clickedDot);

    // Update slide states
    this.updateSlideStates(slideId);

    // Update heading states
    this.updateHeadingStates(slideId);

    // Dispatch custom event for external listeners
    this.dispatchSwatchChangeEvent(slideId);
  }

  updateDotStates(activeDot) {
    // Remove active class from all dots in this swatch
    const allDots = this.querySelectorAll(".embla__dot");
    allDots.forEach((dot) => {
      dot.classList.remove("active");
      dot.setAttribute("aria-selected", "false");
    });

    // Set active dot
    activeDot.classList.add("active");
    activeDot.setAttribute("aria-selected", "true");
  }

  updateSlideStates(slideId) {
    const emblaParentSlide = this.getParentSlide();
    if (!emblaParentSlide) return;

    const swatchContainer = emblaParentSlide.querySelector(
      ".embla-swatch__container"
    );
    if (!swatchContainer) {
      console.warn("Swatch container not found");
      return;
    }

    const slides = swatchContainer.querySelectorAll(".embla-swatch__slide");
    slides.forEach((slide) => {
      const isActive = slide.getAttribute("data-slide-id") === slideId;
      slide.classList.toggle("embla-swatch__slide--active", isActive);
      slide.setAttribute("aria-hidden", !isActive);
    });
  }

  updateHeadingStates(slideId) {
    const emblaParentSlide = this.getParentSlide();
    if (!emblaParentSlide) return;

    const headingContainer = emblaParentSlide.querySelector(
      ".embla-card-heading__container"
    );
    if (!headingContainer) {
      console.warn("Heading container not found");
      return;
    }

    const headingSlides = headingContainer.querySelectorAll(
      ".embla-card-heading__slide"
    );
    headingSlides.forEach((headingSlide) => {
      const isActive = headingSlide.getAttribute("data-slide-id") === slideId;
      headingSlide.classList.toggle(
        "embla-card-heading__slide--active",
        isActive
      );
      headingSlide.setAttribute("aria-hidden", !isActive);
    });
  }

  getParentSlide() {
    const parentSlide = this.closest(".embla-featured-collection__slide");
    const parentCard = this.closest(".product-card-wrapper");
    if (!parentSlide && !parentCard) {
      console.warn("Parent slide and card not found");
    }
    return parentSlide || parentCard;
  }

  dispatchSwatchChangeEvent(slideId) {
    const customEvent = new CustomEvent("swatch:change", {
      detail: {
        swatchId: this.swatchId,
        slideId,
        timestamp: Date.now(),
      },
      bubbles: true,
      cancelable: true,
    });

    this.dispatchEvent(customEvent);
  }

  // Public API methods
  selectSlide(slideId) {
    const targetDot = this.querySelector(`[data-slide-id="${slideId}"]`);
    if (targetDot) {
      this.boundHandlers.dotClick({ target: targetDot });
    } else {
      console.warn(
        `Slide with ID ${slideId} not found in swatch ${this.swatchId}`
      );
    }
  }

  getActiveSlideId() {
    const activeDot = this.querySelector(".embla__dot.active");
    return activeDot ? activeDot.getAttribute("data-slide-id") : null;
  }

  getAllSlideIds() {
    const dots = this.querySelectorAll(".embla__dot[data-slide-id]");
    return Array.from(dots).map((dot) => dot.getAttribute("data-slide-id"));
  }

  cleanup() {
    // Remove event listeners to prevent memory leaks
    const dots = this.querySelectorAll(".embla__dot");
    dots.forEach((dot) => {
      dot.removeEventListener("click", this.boundHandlers.dotClick);
    });
  }

  // Utility method for debugging
  debugInfo() {
    return {
      swatchId: this.swatchId,
      dotCount: this.querySelectorAll(".embla__dot").length,
      activeSlideId: this.getActiveSlideId(),
      allSlideIds: this.getAllSlideIds(),
    };
  }
}

// Register the custom element
customElements.define("custom-swatch", CustomSwatch);

// Export for potential module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = CustomSwatch;
}

// Global utility for debugging (can be removed in production)
if (typeof window !== "undefined") {
  window.CustomSwatch = CustomSwatch;
}
